import { Fragment, useEffect, useRef, useState } from 'react';
import { Check, Square, Timer, X } from 'lucide-react';
import {
  REST_CHOICES, adjustRestTimer, dueAlerts, formatClock, nextSetLine, nextSetText, readKeepAwake, restPhase,
  setRestLength, writeKeepAwake,
} from './rest';
import { goAlert, warnAlert } from './restAlerts';
import { FONT_BODY, FONT_MONO } from './theme';

/* ────────────────────────────────────────────────────────────────────────── */
/*  Rest timer pill (rendered by the root, so it shows on every tab)          */
/* ────────────────────────────────────────────────────────────────────────── */

/* Keeps the screen on while `active`. The browser drops the lock whenever
 * the page is hidden, so it is requested again on the way back. */
function useWakeLock(active) {
  useEffect(() => {
    if (!active || typeof navigator === 'undefined' || !navigator.wakeLock) return undefined;
    let lock = null;
    let cancelled = false;
    const acquire = () => {
      if (cancelled || document.visibilityState !== 'visible' || (lock && !lock.released)) return;
      navigator.wakeLock.request('screen')
        .then(l => { if (cancelled) l.release().catch(() => {}); else lock = l; })
        .catch(() => {});
    };
    acquire();
    document.addEventListener('visibilitychange', acquire);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', acquire);
      if (lock && !lock.released) lock.release().catch(() => {});
    };
  }, [active]);
}

const canWakeLock = () => typeof navigator !== 'undefined' && !!navigator.wakeLock;

function PillButton({ t, label, onClick, children, light }) {
  return (
    <button onClick={onClick} aria-label={label} style={{
      width: 44, height: 44, flexShrink: 0, borderRadius: 10, cursor: 'pointer', padding: 0,
      border: `1px solid ${light ? 'rgba(255,255,255,0.35)' : t.cardBorder}`,
      background: light ? 'rgba(255,255,255,0.14)' : t.surfaceAlt, color: light ? '#fff' : t.text,
      fontFamily: FONT_MONO, fontSize: 13, fontWeight: 700, touchAction: 'manipulation',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>{children}</button>
  );
}

/* A full-width 44px row with a checkbox look. */
function CheckRow({ t, checked, onChange, label, hint }) {
  return (
    <button role="checkbox" aria-checked={checked} onClick={() => onChange(!checked)} style={{
      width: '100%', minHeight: 44, display: 'flex', alignItems: 'center', gap: 10, padding: '0 4px',
      background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', color: t.text,
      fontFamily: FONT_BODY, fontSize: 13, fontWeight: 600,
    }}>
      {checked
        ? <span style={{ width: 20, height: 20, borderRadius: 5, background: t.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Check size={14} color="#fff" /></span>
        : <Square size={20} color={t.textFaint} style={{ flexShrink: 0 }} />}
      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
      {hint && <span style={{ flexShrink: 0, fontSize: 11, fontWeight: 500, color: t.textFaint, fontFamily: FONT_MONO }}>{hint}</span>}
    </button>
  );
}

/**
 * @param {object} props
 * @param {object} props.timer the root's rest timer (rest.js makeRestTimer)
 * @param {(timer: object|null) => void} props.onChange
 * @param {number|null} props.savedSec the plan exercise's restSec (for the hint)
 * @param {number|null} props.autoSec what the plan exercise rests without one
 * @param {((sec: number) => void)|null} props.onRemember writes restSec; null
 *   for exercises outside the plan
 * @param {string|null} props.rememberFor the plan exercise restSec belongs to
 *   (the slot's name, also when today's exercise is a swap)
 */
export function RestPill({ t, timer, onChange, savedSec, autoSec, onRemember, rememberFor }) {
  const [now, setNow] = useState(() => Date.now());
  const [panel, setPanel] = useState(false);
  const [remember, setRemember] = useState(false);
  const [keepAwake, setKeepAwake] = useState(readKeepAwake);
  const fired = useRef({ warnFor: null, goFor: null });

  // Its own tick, so only the pill re-renders while a rest runs.
  useEffect(() => {
    const tick = () => setNow(Date.now());
    const id = window.setInterval(tick, 250);
    document.addEventListener('visibilitychange', tick);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', tick);
    };
  }, []);

  useEffect(() => {
    const due = dueAlerts(timer, now, fired.current);
    fired.current = due.fired;
    if (due.go) goAlert();
    else if (due.tick) warnAlert();
  }, [timer, now]);

  const p = restPhase(timer, now);
  useEffect(() => {
    if (p.phase === 'stale') onChange(null);
  }, [p.phase, onChange]);
  useEffect(() => {
    if (p.phase !== 'running') { setPanel(false); setRemember(false); }
  }, [p.phase]);
  useWakeLock(keepAwake && (p.phase === 'running' || p.phase === 'go' || p.phase === 'over'));

  if (p.phase === 'idle' || p.phase === 'stale') return null;

  const running = p.phase === 'running';
  const go = p.phase === 'go';
  const next = nextSetText(timer.nextSet);
  // Only changes on a phase change or a new length, so screen readers are
  // told "rest over" once instead of every tick.
  const announce = running ? `Rest ${formatClock(timer.total)}` : `Rest over${next ? `. Next: ${next}` : ''}`;
  const pick = (sec) => {
    onChange(setRestLength(timer, sec, Date.now()));
    if (remember && onRemember) onRemember(sec);
    setPanel(false);
    setRemember(false);
  };

  return (
    <div style={{
      position: 'fixed', left: 0, right: 0, bottom: 'calc(max(env(safe-area-inset-bottom), 16px) + var(--fit-bottom, 0px))', zIndex: 50,
      display: 'flex', justifyContent: 'center', padding: '0 16px', pointerEvents: 'none', fontFamily: FONT_BODY,
    }}>
      <style>{`
        @keyframes restGoPulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.025); } }
        .fit-rest-go { animation: restGoPulse 1s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) { .fit-rest-go { animation: none; } }
      `}</style>
      <div role="region" aria-label="Rest timer" style={{ width: '100%', maxWidth: 560, pointerEvents: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <span aria-live="polite" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }}>
          {announce}
        </span>

        {panel && running && (
          <div style={{
            background: t.cardBg, border: `1px solid ${t.cardBorder}`, borderRadius: 16, padding: 12,
            boxShadow: '0 8px 30px rgba(0,0,0,0.35)', display: 'flex', flexDirection: 'column', gap: 8,
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: t.textFaint }}>
              Rest for this set
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 6 }}>
              {REST_CHOICES.map(sec => {
                const on = timer.total === sec;
                return (
                  <button key={sec} onClick={() => pick(sec)} aria-pressed={on} style={{
                    height: 44, borderRadius: 10, cursor: 'pointer', touchAction: 'manipulation',
                    border: `1px solid ${on ? t.accent : t.cardBorder}`, background: on ? t.accentSoft : t.surfaceAlt,
                    color: on ? t.accent : t.text, fontFamily: FONT_MONO, fontSize: 15, fontWeight: 700,
                  }}>{formatClock(sec)}</button>
                );
              })}
            </div>
            {onRemember && (
              <CheckRow t={t} checked={remember} onChange={setRemember}
                label={`Remember for ${rememberFor || timer.exercise || 'this exercise'}`}
                hint={savedSec ? `now ${formatClock(savedSec)}` : autoSec ? `auto ${formatClock(autoSec)}` : null} />
            )}
            {canWakeLock() && (
              <CheckRow t={t} checked={keepAwake} label="Keep screen on during rests"
                onChange={v => { setKeepAwake(v); writeKeepAwake(v); }} />
            )}
          </div>
        )}

        <div className={go ? 'fit-rest-go' : undefined} style={{
          position: 'relative', overflow: 'hidden', minHeight: 64, borderRadius: 16,
          display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px 8px 6px',
          background: go ? t.accent : t.cardBg, color: go ? '#fff' : t.text,
          border: `1px solid ${go ? t.accent : running ? t.accentBorder : t.cardBorder}`,
          boxShadow: '0 8px 30px rgba(0,0,0,0.35)',
        }}>
          {running && (
            <div aria-hidden="true" style={{
              position: 'absolute', left: 0, top: 0, height: 3, width: `${Math.round(p.progress * 1000) / 10}%`,
              background: t.accent, transition: 'width 0.25s linear',
            }} />
          )}

          {running ? (
            <button onClick={() => setPanel(v => !v)} aria-expanded={panel} aria-label="Change rest length"
              style={{
                flex: 1, minWidth: 0, minHeight: 48, display: 'flex', alignItems: 'center', gap: 10, padding: '0 6px',
                background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', color: t.text,
              }}>
              <Timer size={18} color={t.accent} style={{ flexShrink: 0 }} />
              <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <span role="timer" style={{ fontFamily: FONT_MONO, fontSize: 22, fontWeight: 700, lineHeight: 1.1 }}>
                  {formatClock(p.remaining)}
                </span>
                <span style={{ fontSize: 11, color: t.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {next ? `Next · ${nextSetLine(timer)}` : 'Tap to change'}
                </span>
              </span>
            </button>
          ) : (
            <div style={{ flex: 1, minWidth: 0, padding: '0 6px', display: 'flex', flexDirection: 'column', gap: 2 }}>
              {go ? (
                <span style={{
                  fontSize: 15, fontWeight: 700, lineHeight: 1.25, overflow: 'hidden',
                  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                }}>
                  {/* Each part stays whole, so a wrap falls on a separator
                      (kept outside the nowrap spans for that). */}
                  {['GO', ...(next ? next.split(' · ') : [])].map((part, i) => (
                    <Fragment key={i}>{i ? ' · ' : ''}<span style={{ whiteSpace: 'nowrap' }}>{part}</span></Fragment>
                  ))}
                </span>
              ) : (
                <>
                  <span style={{ fontSize: 14, fontWeight: 700 }}>
                    Rest over <span style={{ fontFamily: FONT_MONO }}>{formatClock(p.overBy)}</span> ago
                  </span>
                  {next && (
                    <span style={{ fontSize: 11, color: t.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      Next · {next}
                    </span>
                  )}
                </>
              )}
            </div>
          )}

          {running && (
            <>
              <PillButton t={t} label="15 seconds less" onClick={() => onChange(adjustRestTimer(timer, -15, Date.now()))}>−15</PillButton>
              <PillButton t={t} label="15 seconds more" onClick={() => onChange(adjustRestTimer(timer, 15, Date.now()))}>+15</PillButton>
              <PillButton t={t} label="Skip rest" onClick={() => onChange(null)}>
                <span style={{ fontFamily: FONT_BODY, fontSize: 12 }}>Skip</span>
              </PillButton>
            </>
          )}
          {go && (
            <PillButton t={t} light label="15 seconds more" onClick={() => onChange(adjustRestTimer(timer, 15, Date.now()))}>+15</PillButton>
          )}
          {!running && (
            <PillButton t={t} light={go} label="Dismiss" onClick={() => onChange(null)}><X size={18} /></PillButton>
          )}
        </div>
      </div>
    </div>
  );
}
